import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import TopNav from "../components/TopNav.jsx";

// One admin page for moving dollars: Refuge · Disaster Relief · Overflow config · Payout runs.
// Every action is auditable via the corresponding history/log endpoints.
export default function AdminPoolsPage() {
  const [refuge, setRefuge] = useState(null);
  const [disaster, setDisaster] = useState(null);
  const [overflow, setOverflow] = useState(null);
  const [payoutState, setPayoutState] = useState(null);
  const [refresh, setRefresh] = useState(0);

  const load = useCallback(async () => {
    try {
      const [r, d, o, p] = await Promise.all([
        api.refuge(), api.disasterRelief(), api.overflow(), api.payoutState(),
      ]);
      setRefuge(r); setDisaster(d); setOverflow(o); setPayoutState(p);
    } catch (e) { toast.error(e.message); }
  }, []);

  useEffect(() => { load(); }, [load, refresh]);

  const bump = () => setRefresh((n) => n + 1);

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />
      <div className="mx-auto max-w-5xl px-4 md:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Admin · Pools</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase text-sage-text mb-2">
          Move the dollars.
        </h1>
        <p className="text-sm text-sage-muted mb-8 sage-prose max-w-3xl">
          Contributions and disbursements to the Refuge and Disaster Relief pools, plus edits to the Overflow ceiling and Payout Rail dry-runs. Every change is snapshotted to the corresponding history collection and public on the Ledger.
        </p>

        <div className="flex flex-wrap gap-3 mb-8 text-xs">
          <Link to="/admin" className="border border-sage-border text-sage-muted px-3 py-1 uppercase tracking-[0.1em] hover:border-sage-accent hover:text-sage-accent" data-testid="admin-nav-witness">← Admin (witness log)</Link>
          <Link to="/promises" className="border border-sage-border text-sage-muted px-3 py-1 uppercase tracking-[0.1em] hover:border-sage-accent hover:text-sage-accent" data-testid="admin-nav-promises">Promises →</Link>
          <Link to="/sage-ledger" className="border border-sage-border text-sage-muted px-3 py-1 uppercase tracking-[0.1em] hover:border-sage-accent hover:text-sage-accent" data-testid="admin-nav-ledger">Ledger →</Link>
        </div>

        {refuge && <RefugeCard refuge={refuge} onChange={bump} />}
        {disaster && <DisasterCard disaster={disaster} onChange={bump} />}
        {overflow && <OverflowCard overflow={overflow} onChange={bump} />}
        {payoutState && <PayoutCard state={payoutState} overflow={overflow} onChange={bump} />}
      </div>
    </div>
  );
}

function Section({ title, subtitle, testid, children }) {
  return (
    <section className="border border-sage-border mb-8" data-testid={testid}>
      <div className="p-4 border-b border-sage-border">
        <div className="text-xs uppercase tracking-[0.15em] text-sage-muted">{title}</div>
        <div className="text-sm text-sage-text mt-1 sage-prose">{subtitle}</div>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function RefugeCard({ refuge, onChange }) {
  const [pct, setPct] = useState((refuge.config.pct * 100).toFixed(2));
  const [reason, setReason] = useState("");
  const [amt, setAmt] = useState("");
  const [note, setNote] = useState("");
  const [dRec, setDRec] = useState("");
  const [dPurpose, setDPurpose] = useState("");
  const [dAmt, setDAmt] = useState("");

  const setConfig = async () => {
    const newPct = parseFloat(pct) / 100;
    if (isNaN(newPct) || newPct < 0 || newPct > 1) { toast.error("Percentage must be 0–100."); return; }
    try {
      await api.refugeSetConfig(newPct, refuge.config.scope, reason || "sovereign adjustment");
      toast.success(`Refuge % set to ${(newPct * 100).toFixed(2)}%`);
      setReason(""); onChange();
    } catch (e) { toast.error(e.message); }
  };
  const addContrib = async () => {
    const a = parseFloat(amt);
    if (isNaN(a) || a <= 0) { toast.error("Amount must be positive."); return; }
    try {
      await api.refugeAddContribution(a, "sage_revenue", note || null, null);
      toast.success(`+$${a.toFixed(2)} to Refuge`);
      setAmt(""); setNote(""); onChange();
    } catch (e) { toast.error(e.message); }
  };
  const addDisburs = async () => {
    const a = parseFloat(dAmt);
    if (isNaN(a) || a <= 0) { toast.error("Amount must be positive."); return; }
    if (!dRec || !dPurpose) { toast.error("Recipient + purpose required."); return; }
    try {
      await api.refugeAddDisbursement(a, dRec, dPurpose, true, null, null);
      toast.success(`Disbursed $${a.toFixed(2)}`);
      setDAmt(""); setDRec(""); setDPurpose(""); onChange();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Section
      title="§ 01 · Whistleblower Refuge"
      subtitle={<>Balance <span className="text-sage-accent font-bold tabular-nums">${refuge.balance.balance_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span> · {(refuge.config.pct * 100).toFixed(2)}% of {refuge.config.scope}</>}
      testid="admin-refuge">
      <div className="grid md:grid-cols-3 gap-4 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Set percentage</div>
          <div className="flex gap-2 items-center">
            <input data-testid="refuge-pct-input" type="number" step="0.01" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)} className="w-20 bg-sage-elevated border border-sage-border px-2 py-1 tabular-nums" />
            <span>%</span>
          </div>
          <input data-testid="refuge-pct-reason" placeholder="reason for the change" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-2 w-full bg-sage-elevated border border-sage-border px-2 py-1" />
          <button data-testid="refuge-pct-save" onClick={setConfig} className="mt-2 border border-sage-accent text-sage-accent px-3 py-1 uppercase tracking-[0.1em] hover:bg-sage-accent hover:text-sage-bg">Save %</button>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Log contribution (+)</div>
          <div className="flex gap-2 items-center">
            <span>$</span>
            <input data-testid="refuge-contrib-amt" type="number" step="0.01" min="0" value={amt} onChange={(e) => setAmt(e.target.value)} className="w-24 bg-sage-elevated border border-sage-border px-2 py-1 tabular-nums" />
          </div>
          <input data-testid="refuge-contrib-note" placeholder="note (period, source)" value={note} onChange={(e) => setNote(e.target.value)} className="mt-2 w-full bg-sage-elevated border border-sage-border px-2 py-1" />
          <button data-testid="refuge-contrib-save" onClick={addContrib} className="mt-2 border border-sage-accent text-sage-accent px-3 py-1 uppercase tracking-[0.1em] hover:bg-sage-accent hover:text-sage-bg">Add</button>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Log disbursement (−)</div>
          <input data-testid="refuge-dis-rec" placeholder="recipient (e.g. J. Doe / former OpenAI)" value={dRec} onChange={(e) => setDRec(e.target.value)} className="w-full bg-sage-elevated border border-sage-border px-2 py-1" />
          <input data-testid="refuge-dis-purpose" placeholder="purpose (e.g. SEC filing legal fees)" value={dPurpose} onChange={(e) => setDPurpose(e.target.value)} className="mt-2 w-full bg-sage-elevated border border-sage-border px-2 py-1" />
          <div className="flex gap-2 items-center mt-2">
            <span>$</span>
            <input data-testid="refuge-dis-amt" type="number" step="0.01" min="0" value={dAmt} onChange={(e) => setDAmt(e.target.value)} className="w-24 bg-sage-elevated border border-sage-border px-2 py-1 tabular-nums" />
          </div>
          <button data-testid="refuge-dis-save" onClick={addDisburs} className="mt-2 border border-sage-warn text-sage-warn px-3 py-1 uppercase tracking-[0.1em] hover:bg-sage-warn hover:text-sage-bg">Disburse</button>
        </div>
      </div>
    </Section>
  );
}

function DisasterCard({ disaster, onChange }) {
  const [cAmt, setCAmt] = useState("");
  const [cNote, setCNote] = useState("");
  const [loc, setLoc] = useState("");
  const [org, setOrg] = useState("");
  const [purp, setPurp] = useState("");
  const [dAmt, setDAmt] = useState("");

  const addContrib = async () => {
    const a = parseFloat(cAmt);
    if (isNaN(a) || a <= 0) { toast.error("Amount must be positive."); return; }
    try {
      await api.disasterAddContribution(a, "overflow_share", cNote || null, null);
      toast.success(`+$${a.toFixed(2)} to Disaster Relief`);
      setCAmt(""); setCNote(""); onChange();
    } catch (e) { toast.error(e.message); }
  };
  const addDisburs = async () => {
    const a = parseFloat(dAmt);
    if (isNaN(a) || a <= 0) { toast.error("Amount must be positive."); return; }
    if (!loc || !org || !purp) { toast.error("Location, org, purpose required."); return; }
    try {
      await api.disasterAddDisbursement(a, loc, org, purp, null, null);
      toast.success(`Relief sent · $${a.toFixed(2)}`);
      setDAmt(""); setLoc(""); setOrg(""); setPurp(""); onChange();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Section
      title="§ 02 · Disaster Relief Pool"
      subtitle={<>Balance <span className="text-sage-accent font-bold tabular-nums">${disaster.balance.balance_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span> · {disaster.balance.disbursement_count} disbursement(s) to date</>}
      testid="admin-disaster">
      <div className="grid md:grid-cols-2 gap-4 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Log contribution (+)</div>
          <div className="flex gap-2 items-center">
            <span>$</span>
            <input data-testid="disaster-contrib-amt" type="number" step="0.01" min="0" value={cAmt} onChange={(e) => setCAmt(e.target.value)} className="w-24 bg-sage-elevated border border-sage-border px-2 py-1 tabular-nums" />
          </div>
          <input data-testid="disaster-contrib-note" placeholder="period / note" value={cNote} onChange={(e) => setCNote(e.target.value)} className="mt-2 w-full bg-sage-elevated border border-sage-border px-2 py-1" />
          <button data-testid="disaster-contrib-save" onClick={addContrib} className="mt-2 border border-sage-accent text-sage-accent px-3 py-1 uppercase tracking-[0.1em] hover:bg-sage-accent hover:text-sage-bg">Add</button>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Log disbursement (−)</div>
          <input data-testid="disaster-dis-loc" placeholder="location (e.g. Türkiye / Syria earthquake)" value={loc} onChange={(e) => setLoc(e.target.value)} className="w-full bg-sage-elevated border border-sage-border px-2 py-1" />
          <input data-testid="disaster-dis-org" placeholder="partner org (e.g. Direct Relief)" value={org} onChange={(e) => setOrg(e.target.value)} className="mt-2 w-full bg-sage-elevated border border-sage-border px-2 py-1" />
          <input data-testid="disaster-dis-purpose" placeholder="purpose (e.g. water + medical supplies)" value={purp} onChange={(e) => setPurp(e.target.value)} className="mt-2 w-full bg-sage-elevated border border-sage-border px-2 py-1" />
          <div className="flex gap-2 items-center mt-2">
            <span>$</span>
            <input data-testid="disaster-dis-amt" type="number" step="0.01" min="0" value={dAmt} onChange={(e) => setDAmt(e.target.value)} className="w-24 bg-sage-elevated border border-sage-border px-2 py-1 tabular-nums" />
          </div>
          <button data-testid="disaster-dis-save" onClick={addDisburs} className="mt-2 border border-sage-warn text-sage-warn px-3 py-1 uppercase tracking-[0.1em] hover:bg-sage-warn hover:text-sage-bg">Disburse</button>
        </div>
      </div>
    </Section>
  );
}

function OverflowCard({ overflow, onChange }) {
  const [ceiling, setCeiling] = useState(overflow.ceiling_usd);
  const [disasterPct, setDisasterPct] = useState((overflow.split.disaster_relief_pct * 100).toFixed(2));
  const [maxPayout, setMaxPayout] = useState(overflow.max_payout_per_user_usd);
  const [reason, setReason] = useState("");

  const save = async () => {
    try {
      await api.overflowSetConfig({
        ceiling_usd: parseFloat(ceiling),
        disaster_relief_pct: parseFloat(disasterPct) / 100,
        max_payout_per_user_usd: parseFloat(maxPayout),
      }, reason || "sovereign adjustment");
      toast.success("Overflow config updated · history row logged");
      setReason(""); onChange();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Section
      title="§ 03 · The Overflow · ceiling and split"
      subtitle={<>Ceiling <span className="text-sage-accent font-bold tabular-nums">${overflow.ceiling_usd.toLocaleString()}/yr</span> · currently at {overflow.ceiling_pct?.toFixed(4)}%</>}
      testid="admin-overflow">
      <div className="grid md:grid-cols-3 gap-4 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Ceiling ($ / yr)</div>
          <input data-testid="overflow-ceiling" type="number" step="1000" min="0" value={ceiling} onChange={(e) => setCeiling(e.target.value)} className="w-full bg-sage-elevated border border-sage-border px-2 py-1 tabular-nums" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Disaster Relief %</div>
          <input data-testid="overflow-disaster-pct" type="number" step="0.01" min="0" max="100" value={disasterPct} onChange={(e) => setDisasterPct(e.target.value)} className="w-full bg-sage-elevated border border-sage-border px-2 py-1 tabular-nums" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Max per user ($)</div>
          <input data-testid="overflow-max-payout" type="number" step="1" min="0" value={maxPayout} onChange={(e) => setMaxPayout(e.target.value)} className="w-full bg-sage-elevated border border-sage-border px-2 py-1 tabular-nums" />
        </div>
      </div>
      <input data-testid="overflow-reason" placeholder="reason (goes into history)" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-4 w-full bg-sage-elevated border border-sage-border px-2 py-1 text-xs" />
      <button data-testid="overflow-save" onClick={save} className="mt-2 border border-sage-accent text-sage-accent px-3 py-1 uppercase tracking-[0.1em] text-xs hover:bg-sage-accent hover:text-sage-bg">Save config</button>
    </Section>
  );
}

function PayoutCard({ state, overflow, onChange }) {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [pool, setPool] = useState("");
  const [users, setUsers] = useState("");
  const [note, setNote] = useState("");

  const createRun = async () => {
    const p = parseFloat(pool);
    const u = parseInt(users);
    if (isNaN(p) || p <= 0) { toast.error("Pool must be positive."); return; }
    if (isNaN(u) || u <= 0) { toast.error("Active users must be positive."); return; }
    try {
      const r = await api.payoutCreateRun(period, p, u, null, note || null);
      toast.success(`Run created · $${r.run.per_user_capped_usd.toFixed(2)}/user (capped)`);
      setPool(""); setUsers(""); setNote(""); onChange();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Section
      title="§ 04 · Payout Rail · dry-run"
      subtitle={<>Runs to date · <span className="text-sage-accent font-bold tabular-nums">{state.runs_total}</span> · live transfers <span className="text-sage-warn">{state.live_transfers ? "ON" : "OFF (acquirer deferred)"}</span></>}
      testid="admin-payout">
      <div className="text-xs text-sage-muted sage-prose mb-4">
        {state.notes}
      </div>
      <div className="grid md:grid-cols-4 gap-4 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Period</div>
          <input data-testid="payout-period" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-02" className="w-full bg-sage-elevated border border-sage-border px-2 py-1 tabular-nums" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Pool ($)</div>
          <input data-testid="payout-pool" type="number" step="0.01" min="0" value={pool} onChange={(e) => setPool(e.target.value)} className="w-full bg-sage-elevated border border-sage-border px-2 py-1 tabular-nums" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Active users</div>
          <input data-testid="payout-users" type="number" min="0" value={users} onChange={(e) => setUsers(e.target.value)} className="w-full bg-sage-elevated border border-sage-border px-2 py-1 tabular-nums" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">&nbsp;</div>
          <button data-testid="payout-create" onClick={createRun} className="w-full border border-sage-accent text-sage-accent px-3 py-1 uppercase tracking-[0.1em] hover:bg-sage-accent hover:text-sage-bg">Create dry-run</button>
        </div>
      </div>
      <input data-testid="payout-note" placeholder="note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="mt-3 w-full bg-sage-elevated border border-sage-border px-2 py-1 text-xs" />
      {state.latest_run && (
        <div className="mt-4 text-xs text-sage-faint">
          Latest · {state.latest_run.period} · pool ${state.latest_run.total_pool_usd?.toLocaleString()} · {state.latest_run.active_users} users · {state.latest_run.status}
        </div>
      )}
    </Section>
  );
}
